import clsx from 'clsx';
import { useState } from 'react';
import { BiBookOpen, BiChevronRight, BiPlus } from 'react-icons/bi';
import { Link, useNavigate } from 'react-router-dom';

import { Card, Skeleton } from '@/components';
import { Button } from '@/components/Button';
import ContextMenu from '@/components/ContextMenu/ContextMenu';
import { useAccessGate } from '@/permissions/useAccessGate';
import {
  LOG_BOOKS,
  logBookPath,
  type LogBookRoute,
  LOGS_PATH,
} from '@pages/LogbookPage/logbookRoutes';
import { formatLastWritten, useLogBookStanding } from '@pages/LogbookPage/logbookStanding';
import { logBookHistoryLink } from '@pages/LogbookPage/useLogView';

import s from './LogsCard.module.scss';

/**
 * One row per book: when it was last written, and a way into its history.
 *
 * Its own component because each book reads from its own store, and a book the
 * plan or the role withholds must not fire a request at all.
 */
const LogRow = ({ book }: { book: LogBookRoute }) => {
  const { loading, latest } = useLogBookStanding(book);

  return (
    <li>
      <Link
        className={s.row}
        {...logBookHistoryLink(book)}
        aria-label={
          latest
            ? `${book.short} log: last written ${formatLastWritten(latest).toLowerCase()}`
            : `${book.short} log: never written`
        }
      >
        <span className={s.rowLabel}>{book.rowLabel ?? book.short}</span>
        {loading ? (
          <Skeleton variant="text" width={84} height={16} className={s.skeleton} />
        ) : (
          <span className={latest ? s.rowDate : s.rowNever}>
            {latest ? formatLastWritten(latest) : 'Never'}
          </span>
        )}
      </Link>
    </li>
  );
};

/**
 * Logs — the hub's way into the vessel's record.
 *
 * Built around one action: writing a new entry. Which of the five books is asked
 * after the click rather than spent as five buttons on the card face, so the card
 * stays a shortcut rather than becoming a menu. Underneath, when each book was
 * last written, so it also answers "is the record being kept?" without going
 * anywhere — and each row opens that book's history, where the button opens
 * today's page.
 *
 * Only the books this viewer can actually open are listed; with none of them the
 * card is not drawn at all.
 */
const LogsCard = ({ className, headDivider }: { className?: string; headDivider?: boolean }) => {
  const navigate = useNavigate();
  const { verdictFor } = useAccessGate();
  const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null);

  const books = LOG_BOOKS.filter((book) => verdictFor(book.permission) === 'open');

  if (books.length === 0) return null;

  return (
    <Card as="section" ariaLabel="Logs" className={clsx(s.card, className)}>
      <div className={clsx(s.head, headDivider && s.headDivided)}>
        <BiBookOpen className={s.headIcon} aria-hidden="true" />
        <h2 className="headline-small">Logs</h2>
        <Link className={s.headLink} to={LOGS_PATH}>
          See all logs <BiChevronRight aria-hidden="true" />
        </Link>
      </div>

      {/* `Button` forwards no ref, so the trigger names itself as the menu's
          anchor from the event it fires. */}
      <Button
        aria-expanded={menuAnchor !== null}
        onClick={(event) => {
          const trigger = event.currentTarget;
          setMenuAnchor((anchor) => (anchor ? null : trigger));
        }}
      >
        <BiPlus size={20} /> New log entry
      </Button>

      {/* The book is picked after the click rather than before it, and the choice
          lands on today's page — which is what "new entry" means for a log kept
          by the day. */}
      <ContextMenu
        isOpen={menuAnchor !== null}
        onClose={() => setMenuAnchor(null)}
        anchorEl={menuAnchor}
        items={books.map((book) => ({
          label: `${book.short} log`,
          onClick: () => navigate(logBookPath(book)),
        }))}
      />

      <ul className={s.rows}>
        {books.map((book) => (
          <LogRow key={book.path} book={book} />
        ))}
      </ul>
    </Card>
  );
};

export default LogsCard;
